import {
  Dialog,
  DialogActions,
  DialogContent,
  Typography,
} from "@mui/material";
import { memo, useEffect, ChangeEvent, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { boolean, object } from "yup";
import {
  Box,
  Button,
  Checkbox,
  FlexColBox,
  Form,
  Icon,
  ImageSlider,
  Input,
  Label,
  Link,
  OverlayScrollbarWrapper,
  UploadWrapper,
} from "../../../../common/components";
import { ReactComponent as AttractionLogoSVG } from "./../../../../assets/images/attraction_logo.svg";
import { Select } from "../../../../common/components/Select/Select";
import { DialogTitleWithCloseButton } from "../../../../common/components/Dialog/DialogTitleWithCloseButton";
import { useForm } from "../../../../common/hooks/useForm";
import { useSelector } from "../../../../common/hooks/useSelector";
import { useAppLoading } from "../../../../context/AppLoading/useAppLoading";
import ProjectService from "../../../../service/ProjectService";
import { palette, spacing } from "../../../../styles/theme";
import {
  CreateMockupForm,
  Image,
  DesignFamily,
  DesignProduct,
  ProductLineType,
  Language,
  UserLogo,
  DesignDecoration,
} from "../../../../types";
import { useThemeBreakpoints } from "../../../../utils/themeUtils";
import { toast } from "react-toastify";
import { useAuth } from "../../../../common/hooks/useAuth";
import SelectableBox from "../../../../common/components/Box/SelectableBox";
import { useTenant } from "../../../../context/Tenant";
import AssetService from "../../../../service/asset.service";
import * as designUtils from "../../../../utils/designUtils";

interface Props {
  open: boolean;
  onClose: () => void;
  teamId: string;
  designs?: {
    decorations?: DesignDecoration[];
    designId: string;
    designName: string;
    images: (Image | string)[];
    lockedVersion?: string;
    family?: DesignFamily;
    product?: DesignProduct;
    productLine?: ProductLineType;
  }[];
  projectName: string;
}

const CreateMockupModal = ({
  onClose,
  open,
  projectName,
  teamId,
  designs: designsProp = [],
  ...props
}: Props) => {
  const { t, i18n } = useTranslation();
  const { isMobile } = useThemeBreakpoints();
  const { tenant } = useTenant();
  const userInfo = useSelector((state) => state.userInfo);
  const { setIsAppLoading } = useAppLoading();
  const { isDistributor, isAdmin } = useAuth();
  const [savedLogos, setSavedLogos] = useState<UserLogo[]>([]);

  const containsCustomLabel = useMemo(
    () => designUtils.containsCustomLabel(designsProp[0]?.decorations ?? []),
    [designsProp]
  );

  const loadSavedLogos = async () => {
    if (!teamId) return;
    try {
      const response = await AssetService.getTeamLogos(teamId);
      setSavedLogos(response.data.data || []);
    } catch (error) {
      console.error("Failed to load saved logos:", error);
    }
  };

  const uploadLogo = async (imageData: string, fileName: string) => {
    if (!teamId) {
      return;
    }
    try {
      const response = await AssetService.uploadTeamLogo(
        teamId,
        imageData,
        fileName
      );
      await loadSavedLogos();
      return response.data.data;
    } catch (error) {
      console.error("Failed to upload logo:", error);
      toast.error(t("modal.create_mockup.upload_logo_error"));
      throw error;
    }
  };

  const deleteLogo = async (logoId: string) => {
    if (!teamId) return;
    try {
      await AssetService.deleteTeamLogo(teamId, logoId);
      setSavedLogos((prev) => prev.filter((logo) => logo.logoId !== logoId));
      if (formik.values.customLogoId === logoId) {
        formik.setFieldValue("customLogoId", "");
        formik.setFieldValue("logoType", "logo");
        formik.setFieldValue("logo", tenant.logo.logo);
      }
    } catch (error) {
      console.error("Failed to delete logo:", error);
      toast.error(t("modal.create_mockup.delete_logo_error"));
    }
  };

  const onCreate = async (values: CreateMockupForm) => {
    if (!values.designs.length) {
      toast.error(t("modal.create_mockup.no_designs"));
      return;
    }

    if (isDistributor) {
      values.marketType = "promo";
    }
    // revalide custom logo usage for tenant logo
    // Use custom logo if available
    if (values.customLogo) {
      values.logoType = "custom";
      values.logo = values.customLogo;
    }

    //for tenant logo
    // if (values.logoType === "logo") {
    //   values.logo = await convertToBase64(tenant.logo.logo);
    // } else if (values.logoType === "logomark") {
    //   values.logo = await convertToBase64(tenant.logo.logomark);
    // }
    if (values.logoType === "logo") {
      values.logoType = "jameo";
    } else if (values.logoType === "logomark") {
      values.logoType = "attraction";
    } else if (values.logoType === "none") {
      values.logoType = "none";
    } else if (values.logoType === "custom") {
      if (values.customLogoId) {
        values.logoId = values.customLogoId;
        values.logo = "";
        console.log("Sending custom logo with logoId:", values.logoId);
      } else {
        console.error("Custom logo selected but no customLogoId found!");
        toast.error(t("modal.create_mockup.upload_logo_error"));
        return;
      }
    }

    setIsAppLoading(true);
    await ProjectService.createMockup(values);
    handleClose();
  };

  const initialValues: CreateMockupForm = {
    logoType: "logo",
    logo: tenant.logo.logo,
    marketType: "promo",
    withIntroPage: true,
    withProductsPage: true,
    withClosingPage: true,
    withDetailsProductPage: true,
    withSimpleProductPage: false,
    withOurMagicTouchPage: true,
    includeCustomLabels: true,
    projectName: projectName,
    distributorName: "",
    graphicDesigner: `${userInfo.firstName[0]}${userInfo.lastName[0]}`,
    language: i18n.language as Language,
    currency: userInfo.currency,
    designs: designsProp.map((design) => ({
      ...design,
      proposalNumber: design.lockedVersion ?? "",
    })),
    customLogo: "",
    customLogoId: "",
    includePrices: true,
    includeDistributorPrices: false,
    includeAllPrices: false,
  };

  const handleClose = () => {
    onClose();
    setIsAppLoading(false);
    formik.resetForm({ values: initialValues });
  };

  const formik = useForm({
    initialValues,
    onSubmit: onCreate,
    validateOnChange: true,
    validateOnBlur: true,
    validateOnMount: true,
    validationSchema: object().shape({
      withIntroPage: boolean(),
      withProductsPage: boolean(),
      withDetailsProductPage: boolean(),
      withSimpleProductPage: boolean(),
      withClosingPage: boolean(),
      withOurMagicTouchPage: boolean(),
      includePrices: boolean(),
      includeDistributorPrices: boolean(),
      includeAllPrices: boolean(),
    }),
    validate: (values) => {
      const errors: { [key: string]: string } = {};
      if (
        !values.withIntroPage &&
        !values.withProductsPage &&
        !values.withDetailsProductPage &&
        !values.withSimpleProductPage &&
        !values.withClosingPage &&
        !values.withOurMagicTouchPage
      ) {
        errors.form = t("general.errors.required.at_least_one_option");
      }
      return errors;
    },
  });

  useEffect(() => {
    if (open) {
      formik.resetForm({ values: initialValues });
      if (isAdmin || isDistributor) {
        loadSavedLogos();
      }
    }
    // eslint-disable-next-line
  }, [open]);

  const designs = formik.values.designs as CreateMockupForm["designs"];

  const handleCheckboxChange =
    (name: keyof CreateMockupForm) => (e: ChangeEvent<HTMLInputElement>) => {
      formik.setFieldValue(name, e.target.checked);
    };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth={true}
      maxWidth={"md"}
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          padding: `0 !important`,
        },
      }}
    >
      <OverlayScrollbarWrapper>
        <Box
          sx={{
            padding: {
              xs: `${spacing.wrapperMobile} ${spacing.wrapperMobile} ${spacing.xlMobile}`,
              lg: spacing.wrapperDesktop,
            },
          }}
        >
          <Form formik={formik}>
            <DialogTitleWithCloseButton
              id="customized-dialog-title"
              onClose={handleClose}
            >
              {t("modal.create_mockup.title")}
            </DialogTitleWithCloseButton>
            <DialogContent>
              {designsProp.length > 0 ? (
                <FlexColBox
                  gap={{
                    xs: spacing.regularMobile,
                    lg: spacing.regularDesktop,
                  }}
                >
                  <FlexColBox
                    gap={{
                      xs: spacing.groupingMobile,
                      lg: spacing.groupingDesktop,
                    }}
                    px={"1px"}
                  >
                    {isAdmin && (
                      <Box display={"flex"} gap="12px">
                        <Select
                          name="marketType"
                          label={t("modal.create_mockup.market_type.title")}
                          value={formik.values.marketType}
                          adminOnly
                          options={[
                            {
                              label: t("modal.create_mockup.market_type.promo"),
                              value: "promo",
                            },
                            {
                              label: t(
                                "modal.create_mockup.market_type.recreo"
                              ),
                              value: "recreo",
                            },
                          ]}
                        />
                        <Input
                          noDefaultValue
                          name={`graphicDesigner`}
                          value={formik.values.graphicDesigner}
                          label={t("modal.create_mockup.graphic_designer")}
                          adminOnly
                        />
                      </Box>
                    )}
                    {formik.values.marketType === "promo" &&
                      (isAdmin || isDistributor) && (
                        <Input
                          fullWidth
                          noDefaultValue
                          name={`distributorName`}
                          value={formik.values.distributorName}
                          label={t("modal.create_mockup.distributor_name")}
                        />
                      )}

                    <Input
                      fullWidth
                      noDefaultValue
                      name={`projectName`}
                      value={formik.values.projectName}
                      label={t("modal.create_mockup.project_name")}
                    />

                    <FlexColBox gap={spacing.mediumDesktop} fullWidth>
                      <Label sx={{ textAlign: "start" }}>
                        {t("modal.create_mockup.content")}
                      </Label>
                      <Box display={"flex"}>
                        <FlexColBox gap={spacing.mediumDesktop} width={"50%"}>
                          <Checkbox
                            name="withIntroPage"
                            checked={formik.values.withIntroPage}
                            onChange={handleCheckboxChange("withIntroPage")}
                            label={t("modal.create_mockup.with_intro_page")}
                          />
                          <Checkbox
                            name="withOurMagicTouchPage"
                            checked={formik.values.withOurMagicTouchPage}
                            onChange={handleCheckboxChange(
                              "withOurMagicTouchPage"
                            )}
                            label={t(
                              "modal.create_mockup.with_our_magic_touch"
                            )}
                          />
                          <Checkbox
                            name="withProductsPage"
                            checked={formik.values.withProductsPage}
                            onChange={handleCheckboxChange("withProductsPage")}
                            label={t("modal.create_mockup.with_products_page")}
                          />
                          <Checkbox
                            name="withClosingPage"
                            checked={formik.values.withClosingPage}
                            onChange={handleCheckboxChange("withClosingPage")}
                            label={t("modal.create_mockup.with_closing_page")}
                          />
                        </FlexColBox>
                        <FlexColBox gap={spacing.mediumDesktop} width={"50%"}>
                          <FlexColBox gap={"4px"}>
                            <Checkbox
                              name="withDetailsProductPage"
                              checked={formik.values.withDetailsProductPage}
                              onChange={handleCheckboxChange(
                                "withDetailsProductPage"
                              )}
                              label={t(
                                "modal.create_mockup.with_details_product_page"
                              )}
                            />
                            <Checkbox
                              name="includePrices"
                              checked={formik.values.includePrices}
                              onChange={handleCheckboxChange("includePrices")}
                              containerSx={{
                                pl: "24px",
                              }}
                              label={t(
                                isAdmin || isDistributor
                                  ? "modal.create_mockup.include_prices_with_msrp"
                                  : "modal.create_mockup.include_prices"
                              )}
                            />
                            {(isAdmin || isDistributor) && (
                              <Checkbox
                                name="includeDistributorPrices"
                                checked={formik.values.includeDistributorPrices}
                                containerSx={{
                                  pl: "24px",
                                }}
                                onChange={handleCheckboxChange(
                                  "includeDistributorPrices"
                                )}
                                label={t(
                                  "modal.create_mockup.include_distributor_prices"
                                )}
                              />
                            )}
                            {isAdmin && (
                              <Checkbox
                                name="includeAllPrices"
                                checked={formik.values.includeAllPrices}
                                containerSx={{
                                  pl: "24px",
                                }}
                                onChange={handleCheckboxChange(
                                  "includeAllPrices"
                                )}
                                label={t(
                                  "modal.create_mockup.include_all_prices"
                                )}
                              />
                            )}
                            {(isAdmin || isDistributor) &&
                              containsCustomLabel && (
                                <Checkbox
                                  name="includeCustomLabels"
                                  checked={formik.values.includeCustomLabels}
                                  onChange={handleCheckboxChange(
                                    "includeCustomLabels"
                                  )}
                                  containerSx={{
                                    pl: "24px",
                                  }}
                                  label={t(
                                    "modal.create_mockup.include_custom_labels"
                                  )}
                                />
                              )}
                          </FlexColBox>
                          <Checkbox
                            name="withSimpleProductPage"
                            checked={formik.values.withSimpleProductPage}
                            onChange={handleCheckboxChange(
                              "withSimpleProductPage"
                            )}
                            label={t(
                              "modal.create_mockup.with_simple_product_page"
                            )}
                          />
                        </FlexColBox>
                      </Box>
                    </FlexColBox>
                    <Box display={"flex"} gap={spacing.mediumDesktop}>
                      <Select
                        name="language"
                        label={t("language.title")}
                        value={formik.values.language}
                        noMaxWidth
                        options={[
                          {
                            label: t("language.en"),
                            value: "en",
                          },
                          {
                            label: t("language.fr"),
                            value: "fr",
                          },
                        ]}
                      />
                      {(formik.values.includePrices ||
                        formik.values.includeDistributorPrices) && (
                        <Select
                          name="currency"
                          label={t("general.currency.title")}
                          value={formik.values.currency}
                          options={[
                            {
                              label: t("general.currency.usd"),
                              value: "usd",
                            },
                            {
                              label: t("general.currency.cad"),
                              value: "cad",
                            },
                          ]}
                        />
                      )}
                    </Box>

                    {(isAdmin || isDistributor) && (
                      <Box>
                        <Label sx={{ mb: 0.5 }}>
                          {t("modal.create_mockup.logo")}
                        </Label>
                        <Box
                          display={"flex"}
                          gap={"12px"}
                          fullWidth
                          flexWrap={"wrap"}
                        >
                          <SelectableBox
                            text={
                              <Box
                                minWidth={"82px"}
                                maxWidth={"200px"}
                                maxHeight={"24px"}
                              >
                                <img src={tenant.logo.logo} alt="logo" />
                              </Box>
                            }
                            onClick={() => {
                              if (formik.values.logoType !== "logo") {
                                formik.setFieldValue("logoType", "logo");
                                formik.setFieldValue("logo", tenant.logo.logo);
                              }
                            }}
                            active={formik.values.logoType === "logo"}
                          />
                          <SelectableBox
                            text={<AttractionLogoSVG />}
                            onClick={() => {
                              if (formik.values.logoType !== "logomark") {
                                formik.setFieldValue("logoType", "logomark");
                                formik.setFieldValue(
                                  "logo",
                                  tenant.logo.logomark
                                );
                              }
                            }}
                            active={formik.values.logoType === "logomark"}
                          />
                          {savedLogos.map((savedLogo) => {
                            return (
                              <SelectableBox
                                key={savedLogo.logoId}
                                text={
                                  <img
                                    src={savedLogo.url}
                                    alt="saved logo"
                                    height={24}
                                    width={"100%"}
                                    style={{ maxWidth: "217px" }}
                                  />
                                }
                                onClick={() => {
                                  formik.setFieldValue(
                                    "customLogoId",
                                    savedLogo.logoId
                                  );
                                  formik.setFieldValue("logoType", "custom");
                                  formik.setFieldValue("logo", savedLogo.url);
                                }}
                                endIcon={
                                  <Icon
                                    icon="close"
                                    size={24}
                                    color={palette.grey.primary}
                                    onClick={async (e) => {
                                      e.stopPropagation();
                                      await deleteLogo(savedLogo.logoId);
                                    }}
                                  />
                                }
                                active={
                                  formik.values.logoType === "custom" &&
                                  savedLogo.logoId ===
                                    formik.values.customLogoId
                                }
                              />
                            );
                          })}
                          <SelectableBox
                            text={t("modal.create_mockup.no_logo")}
                            onClick={() => {
                              if (formik.values.logoType !== "none") {
                                formik.setFieldValue("logoType", "none");
                                formik.setFieldValue("logo", "");
                              }
                            }}
                            active={formik.values.logoType === "none"}
                            startIcon={
                              <Icon
                                icon="crop_free"
                                size={24}
                                color={palette.grey.primary}
                              />
                            }
                          />
                          <UploadWrapper
                            acceptedFileExtensions={[
                              ".svg",
                              ".png",
                              ".jpg",
                              ".jpeg",
                            ]}
                            onUpload={async (options) => {
                              const file = options.file;
                              const reader = new FileReader();
                              reader.onloadend = async () => {
                                const base64String = reader.result as string;
                                try {
                                  const uploadedLogo = await uploadLogo(
                                    base64String,
                                    (file as File).name
                                  );
                                  if (!uploadedLogo) return;
                                  formik.setFieldValue(
                                    "customLogoId",
                                    uploadedLogo.logoId
                                  );
                                  formik.setFieldValue("logoType", "custom");
                                  formik.setFieldValue(
                                    "logo",
                                    uploadedLogo.url
                                  );
                                } catch (error) {
                                  console.error("Upload failed:", error);
                                }
                              };
                              reader.readAsDataURL(file as Blob);
                            }}
                            onSuccess={(res, file) => {}}
                          >
                            <SelectableBox
                              text={t("modal.create_mockup.upload_logo")}
                              onClick={() => {}}
                              sx={{
                                height: "58px",
                              }}
                              startIcon={
                                <Icon
                                  icon="upload"
                                  size={24}
                                  color={palette.grey.primary}
                                />
                              }
                              active={false}
                            />
                          </UploadWrapper>
                        </Box>
                      </Box>
                    )}
                  </FlexColBox>

                  <FlexColBox
                    gap={{
                      xs: spacing.regularMobile,
                      lg: "12px",
                    }}
                  >
                    <Label sx={{ mb: 0.5 }}>
                      {t("general.product", {
                        count: designs.length,
                      })}
                    </Label>
                    {designs.map((d, index) => (
                      <Box
                        display={"flex"}
                        gap={spacing.regularDesktop}
                        key={d.designId}
                        sx={{
                          padding: {
                            xs: spacing.wrapperMobile,
                            lg: spacing.wrapperDesktop,
                          },
                          border: `1px solid ${palette.grey.midGrey}`,
                          borderRadius: "8px",
                          background: `white`,
                        }}
                      >
                        <Box display={"flex"}>
                          <ImageSlider
                            images={d.images}
                            containerWidth={isMobile ? "115px" : "176px"}
                            containerHeight={isMobile ? "100px" : "176px"}
                          />
                        </Box>
                        <FlexColBox
                          gap={{
                            xs: spacing.groupingMobile,
                            lg: spacing.groupingDesktop,
                          }}
                          width={"100%"}
                          minWidth={"0"}
                        >
                          <Box
                            display={"flex"}
                            gap={spacing.regularDesktop}
                            alignItems={"center"}
                            justifyContent={"space-between"}
                          >
                            <Typography
                              variant="h2"
                              maxWidth={"100%"}
                              textOverflow={"ellipsis"}
                              overflow={"hidden"}
                              whiteSpace={"nowrap"}
                            >
                              {d.designName}
                            </Typography>
                            <Link
                              onClick={(e) => {
                                e.preventDefault();
                                formik.setFieldValue(
                                  `designs`,
                                  formik.values.designs.filter(
                                    (design: CreateMockupForm["designs"][0]) =>
                                      design.designId !== d.designId
                                  )
                                );
                              }}
                            >
                              {t("general.remove")}
                            </Link>
                          </Box>
                          <Input
                            fullWidth
                            noDefaultValue
                            name={`designs.${index}.proposalNumber`}
                            value={formik.values.designs[index].proposalNumber}
                            label={t("modal.create_mockup.proposal_number")}
                            onChange={(e) => {
                              formik.setFieldValue(
                                `designs.${index}.proposalNumber`,
                                e.target.value
                              );
                            }}
                          />
                        </FlexColBox>
                      </Box>
                    ))}
                  </FlexColBox>

                  {isMobile && (
                    <Button
                      type="submit"
                      fullWidth
                      disabled={
                        !formik.isValid || formik.values.designs.length === 0
                      }
                    >
                      <Typography variant="actionBoldBigIcon">
                        {t("modal.create_mockup.title")}
                      </Typography>
                    </Button>
                  )}
                </FlexColBox>
              ) : (
                <Box>
                  <Typography
                    sx={{
                      fontSize: "16px",
                      fontWeight: "400",
                      color: palette.grey.primary,
                      mb: "24px",
                    }}
                  >
                    {t("modal.create_mockup.no_designs_message")}
                  </Typography>
                </Box>
              )}
            </DialogContent>
            {!isMobile && designsProp.length > 0 && (
              <DialogActions sx={{ justifyContent: "center", mt: "32px" }}>
                <Button
                  type="submit"
                  disabled={
                    !formik.isValid || formik.values.designs.length === 0
                  }
                >
                  <Typography variant="actionBoldBigIcon">
                    {t("modal.create_mockup.title")}
                  </Typography>
                </Button>
              </DialogActions>
            )}
          </Form>
        </Box>
      </OverlayScrollbarWrapper>
    </Dialog>
  );
};

export default memo(CreateMockupModal);
